@if (isVisible()) {
<div class="fixed inset-0 z-99999" role="presentation">

  <!-- Light backdrop: the dashboard stays visible behind the guide -->
  <div class="welcome-backdrop absolute inset-0 bg-gray-900/30 dark:bg-black/50" (click)="close()"></div>

  <!-- Panel: bottom sheet on mobile, floating side panel from sm up -->
  <section
    class="welcome-panel absolute inset-x-0 bottom-0 flex max-h-[88dvh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl
           sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-4 sm:max-h-[calc(100dvh-2rem)] sm:w-[400px] sm:rounded-2xl
           dark:bg-gray-900 dark:ring-1 dark:ring-white/10"
    role="dialog"
    aria-modal="true"
    aria-labelledby="welcome-title"
    aria-describedby="welcome-desc">

    <!-- Header -->
    <header class="relative shrink-0 bg-gradient-to-br from-brand-500 to-[#6C4DFF] px-5 pb-5 pt-4 text-white sm:px-6 sm:pt-5">
      <!-- Grab handle (mobile only) -->
      <div class="mx-auto mb-3 h-1 w-10 rounded-full bg-white/40 sm:hidden" aria-hidden="true"></div>

      <button
        type="button"
        (click)="close()"
        class="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Cerrar guía de inicio">
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <h2 id="welcome-title" class="pr-10 text-xl font-semibold leading-tight sm:text-2xl">
        @if (userName()) {
          Hola, {{ userName() }}
        } @else {
          Te damos la bienvenida
        }
      </h2>
      <p id="welcome-desc" class="mt-1.5 max-w-[34ch] text-sm leading-relaxed text-white/85">
        @if (isComplete()) {
          Tu negocio ya está listo para recibir citas.
        } @else {
          Con estos tres pasos tu agenda queda lista para recibir citas.
        }
      </p>

      <!-- Progress: one segment per step -->
      <div class="mt-4 flex items-center gap-3">
        <div class="flex flex-1 gap-1.5" aria-hidden="true">
          @for (step of steps; track step.key) {
          <span class="h-1.5 flex-1 rounded-full transition-colors duration-500"
            [class]="status()[step.key] ? 'bg-white' : 'bg-white/25'"></span>
          }
        </div>
        <span class="text-xs font-medium tabular-nums text-white/90">
          {{ completedSteps() }} de {{ totalSteps }} listos
        </span>
      </div>
    </header>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-3 py-3 sm:px-4">
      <ol class="space-y-1.5">
        @for (step of steps; track step.key; let i = $index) {
        @let done = status()[step.key];
        @let isNext = nextStepKey() === step.key;
        <li>
          <button
            type="button"
            (click)="goTo(step.route)"
            class="group flex w-full items-center gap-3.5 rounded-xl px-3 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            [class]="isNext
              ? 'bg-brand-50 ring-1 ring-brand-200 hover:bg-brand-100 dark:bg-brand-500/10 dark:ring-brand-500/30 dark:hover:bg-brand-500/15'
              : 'hover:bg-gray-50 dark:hover:bg-white/5'">

            <!-- Step marker: number, or check when done -->
            @if (done) {
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success-500 text-white">
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            } @else {
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
              [class]="isNext
                ? 'bg-brand-500 text-white'
                : 'border border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400'">
              {{ i + 1 }}
            </span>
            }

            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium"
                [class]="done ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-white/90'">
                {{ step.title }}
              </span>
              <span class="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                {{ done ? 'Listo. Puedes editarlo cuando quieras.' : step.hint }}
              </span>
            </span>

            <svg class="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 dark:text-gray-500"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </li>
        }
      </ol>

      <!-- Optional intro video, collapsed by default -->
      @if (videoUrl) {
      <div class="mt-3 border-t border-gray-100 px-1 pt-3 dark:border-gray-800">
        <button
          type="button"
          (click)="toggleVideo()"
          [attr.aria-expanded]="videoOpen()"
          aria-controls="welcome-video"
          class="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300 dark:hover:bg-white/5">
          <span class="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            <svg class="ml-0.5 h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <span class="flex-1">{{ videoOpen() ? 'Ocultar video' : 'Ver video de introducción' }}</span>
          <svg class="h-4 w-4 text-gray-400 transition-transform" [class.rotate-180]="videoOpen()"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        @if (videoOpen()) {
        <div id="welcome-video" class="mt-2 overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800">
          <div class="relative aspect-video">
            <iframe
              class="absolute inset-0 h-full w-full"
              [src]="videoUrl"
              title="Introducción a CitaLink Pro"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen>
            </iframe>
          </div>
        </div>
        }
      </div>
      }
    </div>

    <!-- Footer -->
    <footer class="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 dark:border-gray-800 sm:px-6">
      <p class="text-xs leading-snug text-gray-500 dark:text-gray-400">
        También la encuentras en tu menú de usuario.
      </p>
      <button
        type="button"
        (click)="close()"
        class="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300 dark:hover:bg-white/5">
        {{ isComplete() ? 'Cerrar' : 'Ahora no' }}
      </button>
    </footer>
  </section>
</div>
}

<!-- Reopen pill: visible while setup steps are pending and the panel is closed -->
@if (showPill()) {
<div class="welcome-pill fixed bottom-4 right-4 z-9999 flex items-center rounded-full bg-white shadow-lg ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-white/10">
  <button
    type="button"
    (click)="open()"
    class="flex items-center gap-2.5 rounded-full py-2 pl-2 pr-3 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-white/90 dark:hover:bg-white/5"
    aria-label="Abrir guía de inicio">
    <span class="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-[#6C4DFF] text-xs font-semibold tabular-nums text-white">
      {{ completedSteps() }}/{{ totalSteps }}
    </span>
    Guía de inicio
  </button>
  <button
    type="button"
    (click)="hidePill()"
    class="mr-1 flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-white/5 dark:hover:text-gray-300"
    aria-label="Ocultar acceso a la guía de inicio">
    <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
</div>
}
